import React, { useState, useEffect } from 'react';
import { UserCircle } from 'lucide-react';

// Three-tier fallback: image -> gold initials -> generic icon.
// Used by the property detail seller card, inbox rows, agent cards, etc.

function getInitials(name) {
  if (!name || typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const first = parts[0].charAt(0);
  const last = parts[parts.length - 1].charAt(0);
  return (first + last).toUpperCase();
}

export default function Avatar({
  avatarUrl,
  name,
  size = 46,
  className = '',
  style: styleOverride = {},
}) {
  const [imgFailed, setImgFailed] = useState(false);

  // Reset failure state when the URL changes so a previously broken slot
  // gets another chance with a new src.
  useEffect(() => { setImgFailed(false); }, [avatarUrl]);

  const dimension = typeof size === 'number' ? `${size}px` : size;
  const fontSize = Math.max(12, Math.round((typeof size === 'number' ? size : 46) * 0.39));
  const iconSize = Math.max(16, Math.round((typeof size === 'number' ? size : 46) * 0.52));

  const baseStyle = {
    width: dimension,
    height: dimension,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    lineHeight: 1,
    ...styleOverride,
  };

  if (avatarUrl && !imgFailed) {
    return (
      <div className={className} style={baseStyle}>
        <img
          src={avatarUrl}
          alt={name || ''}
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  const initials = getInitials(name);

  if (initials) {
    return (
      <div
        className={className}
        style={{
          ...baseStyle,
          background: '#B8924A',
          color: '#0E0E10',
          border: '1px solid #B8924A',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: `${fontSize}px`,
          fontWeight: 600,
          letterSpacing: '0.5px',
        }}
        aria-label={name}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        ...baseStyle,
        background: '#F3EEFF',
        color: '#0E0E10',
        border: '1px solid #E9DDC1',
      }}
      aria-label="User"
    >
      <UserCircle size={iconSize} strokeWidth={1.5} />
    </div>
  );
}

export { getInitials };
